import { extractIacOps } from "./index.js";
import { collectWindowOps, groupActivity, distinctKey } from "../../src/build/index.js";
import { reconstructState } from "../../src/build/reconstruct.js";

export const extract = extractIacOps;
const RANK = { applied: 4, planned: 3, "no-change": 2, failed: 2, answered: 2, unrecorded: 1 };
export const informedness = (op) => (RANK[op.outcome] || 0) * 10000 + (op.output?.length || 0);
export const collect = (prompts) => collectWindowOps(prompts, { informedness });

export const groupBy = (op) => op.stack || distinctKey("unnamed stack", op);

// A plan is a PROPOSAL, not state, so it is recorded as a change rather than an
// observation: the gap between planning and applying is the whole reading.
export const stateOf = (flat) => reconstructState(flat, {
  target: groupBy,
  at: (op) => op.ts,
  read: (op) => (op.outcome === "applied" && op.counts
    ? { applied: `${op.counts.add}/${op.counts.change}/${op.counts.destroy}` }
    : op.outcome === "no-change" ? { applied: "matches configuration" } : null),
  change: (op) => (op.kind === "plan" && op.touched
    ? `planned ${op.touched} resource${op.touched > 1 ? "s" : ""}${op.destroys ? ` (${op.destroys} destroyed)` : ""}`
    : null),
});

export const build = (flat) => {
  const recon = stateOf(flat);
  const byTarget = new Map(recon.targets.map((t) => [t.target, t]));
  const built = grouped(flat);
  for (const g of built.groups) {
    const e = byTarget.get(g.key);
    if (!e) continue;
    g.changes = e.changes.map((c) => ({ what: c.what, at: c.at, checked: c.checked, stateDiffered: c.stateDiffered }));
    g.unchecked = e.unchecked;
    // A plan nobody applied is a decision left open, and it is the finding.
    if (e.unchecked) g.labels = [`${e.unchecked} plan${e.unchecked > 1 ? "s" : ""} never applied`, ...g.labels];
  }
  return built;
};

const grouped = (flat) => groupActivity(flat, {
  key: groupBy,
  label: (op) => op.stack,
  seed: () => ({ kinds: {}, outcomes: {}, labels: [], biggest: 0, destroys: 0, tools: [] }),
  fold: (g, op) => {
    g.kinds[op.kind] = (g.kinds[op.kind] || 0) + 1;
    g.outcomes[op.outcome] = (g.outcomes[op.outcome] || 0) + 1;
    if (op.touched && op.touched > g.biggest) g.biggest = op.touched;
    if (op.destroys) g.destroys += op.destroys;
    if (!g.tools.includes(op.tool)) g.tools.push(op.tool);
  },
  finish: (g) => {
    g.labels = [
      g.biggest ? `largest change ${g.biggest} resources` : null,
      g.destroys ? `${g.destroys} would be destroyed` : null,
      ...g.tools,
    ].filter(Boolean);
  },
});

export const anchor = () => "plan";
export const ignore = ["output", "steps", "checks"];
